// frontend/src/components/admin/DataProviderConfigTable.js

import React, { useState, useEffect } from 'react';
import {
  RefreshCw,
  Database,
  AlertTriangle,
  CheckCircle2,
  ChevronDown,
  Info,
} from 'lucide-react';
import useAdminStore from '../../store/adminStore';
import { toast } from 'react-hot-toast';

const PROVIDERS = [
  { value: 'ALRAHUZDATA', label: 'Alrahuzdata' },
  { value: 'ASBDATA', label: 'ASBDATA' },
  { value: 'SMEPLUG', label: 'SMEPlug' },
];

const NETWORK_INFO = {
  MTN: { color: 'bg-yellow-50 text-yellow-700 border-yellow-200' },
  AIRTEL: { color: 'bg-red-50 text-red-700 border-red-200' },
  GLO: { color: 'bg-green-50 text-green-700 border-green-200' },
  ETISALAT: { color: 'bg-purple-50 text-purple-700 border-purple-200' },
};

const DataProviderConfigTable = () => {
  const {
    dataProviderConfig,
    getDataProviderConfig,
    updateDataProviderConfig,
    airtimeProviderConfig,
    getAirtimeProviderConfig,
    updateAirtimeProviderConfig,
    isLoading,
  } = useAdminStore();

  const [airtimeConfirm, setAirtimeConfirm] = useState(null);
  const [confirmState, setConfirmState] = useState(null); // { network, newProvider, oldProvider }
  const [localSelections, setLocalSelections] = useState({});

  useEffect(() => {
    getDataProviderConfig().catch(err => {
      console.error('Failed to load data provider config:', err);
    });
    getAirtimeProviderConfig().catch(err => {
      console.error('Failed to load airtime provider config:', err);
    });
  }, []);

  // Sync local selections whenever DB state changes
  useEffect(() => {
    const next = {};
    dataProviderConfig.forEach(row => {
      next[row.network] = row.providerType;
    });
    setLocalSelections(next);
  }, [dataProviderConfig]);

  const handleSelect = (network, newProvider) => {
    const current = localSelections[network];
    if (newProvider === current) return;

    // Update local dropdown immediately (so the UI reflects the choice)
    setLocalSelections(prev => ({ ...prev, [network]: newProvider }));

    // Open confirm modal
    setConfirmState({
      network,
      oldProvider: current,
      newProvider,
    });
  };

  const handleConfirm = async () => {
    if (!confirmState) return;
    const { network, newProvider } = confirmState;

    try {
      await updateDataProviderConfig(network, newProvider);
      setConfirmState(null);
    } catch (error) {
      // Revert local selection on failure
      setLocalSelections(prev => ({
        ...prev,
        [network]: confirmState.oldProvider,
      }));
      setConfirmState(null);
    }
  };

  const handleCancel = () => {
    if (!confirmState) return;
    const { network, oldProvider } = confirmState;
    setLocalSelections(prev => ({ ...prev, [network]: oldProvider }));
    setConfirmState(null);
  };

  const handleRefresh = async () => {
    try {
      await getDataProviderConfig();
      toast.success('Configuration refreshed');
    } catch (error) {
      // toast already shown by store
    }
  };

  return (
    <div className="space-y-4">
      {/* ============ AIRTIME PROVIDER CARD ============ */}
      <div className="bg-white rounded-xl shadow-neomorph p-4 border border-primary-100/30">
        <div className="flex items-center mb-3">
          <div className="w-1 h-5 bg-gradient-to-b from-green-400 to-green-600 rounded-full mr-2" />
          <div>
            <h3 className="text-base font-semibold text-gray-800">
              Airtime Provider
            </h3>
            <p className="text-xs text-gray-500">
              Global setting — applies to all networks
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3 flex-wrap">
          {['ALRAHUZDATA', 'VTPASS'].map((p) => {
            const isCurrent = airtimeProviderConfig?.providerType === p;
            return (
              <button
                key={p}
                disabled={isLoading || isCurrent}
                onClick={() => setAirtimeConfirm(p)}
                className={`
                  px-4 py-2 rounded-lg text-sm font-medium border transition-all
                  ${isCurrent
                    ? 'bg-gradient-to-r from-green-500 to-green-600 text-white border-transparent shadow-md cursor-default'
                    : 'bg-white text-gray-700 border-gray-200 shadow-neomorph hover:shadow-neomorph-hover'
                  }
                  disabled:opacity-60
                `}
              >
                {p === 'ALRAHUZDATA' ? 'Alrahuzdata' : 'VTpass'}
                {isCurrent && <span className="ml-2 text-xs">● Active</span>}
              </button>
            );
          })}
        </div>

        {airtimeProviderConfig?.updatedAt && (
          <p className="text-[11px] text-gray-400 mt-3">
            Last changed:{' '}
            {new Date(airtimeProviderConfig.updatedAt).toLocaleString('en-NG', {
              dateStyle: 'medium',
              timeStyle: 'short',
            })}
          </p>
        )}
      </div>

      {/* Header card */}
      <div className="bg-white rounded-xl shadow-neomorph p-3 border border-primary-100/30">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
          <div className="flex items-center">
            <div className="w-1 h-5 bg-gradient-to-b from-purple-400 to-purple-600 rounded-full mr-2" />
            <div>
              <h3 className="text-base font-semibold text-gray-800">
                Data Provider Routing
              </h3>
              <p className="text-xs text-gray-500">
                Choose which VTU provider fulfills data purchases for each network
              </p>
            </div>
          </div>
          <button
            onClick={handleRefresh}
            disabled={isLoading}
            className="px-3 py-1.5 bg-white text-gray-700 font-medium rounded-lg shadow-neomorph hover:shadow-neomorph-hover transition-all flex items-center disabled:opacity-50 text-xs"
          >
            <RefreshCw className={`h-3 w-3 mr-1.5 ${isLoading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {/* Info banner */}
      <div className="bg-blue-50 rounded-xl p-3 border border-blue-200/60 shadow-neomorph-inset">
        <div className="flex items-start">
          <div className="p-1.5 bg-blue-100 rounded-lg mr-2.5 flex-shrink-0">
            <Info className="h-4 w-4 text-blue-600" />
          </div>
          <div className="text-xs text-blue-800 leading-relaxed">
            <p className="font-medium mb-0.5">Changes take effect immediately.</p>
            <p className="text-blue-700">
              Users will see the selected provider&apos;s plans for that network on
              their next page load. In-flight transactions finish on the provider
              they were started with. We recommend switching during off-peak hours.
            </p>
          </div>
        </div>
      </div>

      {/* Config table */}
      <div className="bg-white rounded-xl shadow-neomorph border border-primary-100/30 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-primary-100/50 text-xs">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left font-medium text-gray-500 uppercase tracking-wider">
                  Network
                </th>
                <th className="px-4 py-3 text-left font-medium text-gray-500 uppercase tracking-wider">
                  Active Provider
                </th>
                <th className="px-4 py-3 text-left font-medium text-gray-500 uppercase tracking-wider">
                  Last Updated
                </th>
                <th className="px-4 py-3 text-left font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-primary-100/50">
              {isLoading && dataProviderConfig.length === 0 ? (
                <tr>
                  <td colSpan="4" className="px-4 py-8 text-center">
                    <div className="flex items-center justify-center space-x-2">
                      <RefreshCw className="h-4 w-4 text-primary-500 animate-spin" />
                      <span className="text-xs text-gray-500">Loading...</span>
                    </div>
                  </td>
                </tr>
              ) : dataProviderConfig.length === 0 ? (
                <tr>
                  <td colSpan="4" className="px-4 py-8 text-center">
                    <Database className="h-6 w-6 text-gray-400 mx-auto mb-2" />
                    <p className="text-xs text-gray-500">
                      No provider configuration found
                    </p>
                  </td>
                </tr>
              ) : (
                dataProviderConfig.map((row) => {
                  const networkMeta = NETWORK_INFO[row.network] || {
                    color: 'bg-gray-50 text-gray-700 border-gray-200',
                  };
                  const currentValue = localSelections[row.network] || row.providerType;
                  const isDirty = currentValue !== row.providerType;

                  return (
                    <tr
                      key={row.network}
                      className="hover:bg-gray-50 transition-colors"
                    >
                      {/* Network */}
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span
                          className={`inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-semibold border ${networkMeta.color}`}
                        >
                          {row.network}
                        </span>
                      </td>

                      {/* Provider dropdown */}
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="relative inline-block">
                          <select
                            value={currentValue}
                            onChange={(e) =>
                              handleSelect(row.network, e.target.value)
                            }
                            disabled={isLoading}
                            className={`
                              appearance-none pl-3 pr-9 py-1.5 rounded-lg text-xs font-medium
                              shadow-neomorph-inset border border-primary-100/30
                              focus:outline-none focus:ring-2 focus:ring-primary-500/20
                              disabled:opacity-50 cursor-pointer
                              ${isDirty ? 'text-primary-700 bg-primary-50' : 'text-gray-800 bg-white'}
                            `}
                          >
                            {PROVIDERS.map((p) => (
                              <option key={p.value} value={p.value}>
                                {p.label}
                              </option>
                            ))}
                          </select>
                          <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400 pointer-events-none" />
                        </div>
                      </td>

                      {/* Last updated */}
                      <td className="px-4 py-3 whitespace-nowrap text-xs text-gray-500">
                        {row.updatedAt
                          ? new Date(row.updatedAt).toLocaleString('en-NG', {
                              dateStyle: 'medium',
                              timeStyle: 'short',
                            })
                          : '—'}
                      </td>

                      {/* Status badge */}
                      <td className="px-4 py-3 whitespace-nowrap">
                        {row.isActive ? (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium bg-green-50 text-green-700 border border-green-200">
                            <CheckCircle2 className="h-3 w-3 mr-1" />
                            Active
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium bg-red-50 text-red-700 border border-red-200">
                            <AlertTriangle className="h-3 w-3 mr-1" />
                            Inactive
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Confirm modal */}
      {confirmState && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-3 pt-3 pb-16 text-center sm:block sm:p-0">
            <div
              className="fixed inset-0 transition-opacity bg-gray-600/50 backdrop-blur-sm"
              onClick={handleCancel}
            />

            <div className="inline-block align-bottom bg-[#E8ECF5] rounded-xl shadow-neomorph px-4 pt-4 pb-4 text-left overflow-hidden transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-5 border border-primary-100/30">
              <div className="sm:flex sm:items-start">
                <div className="mx-auto flex-shrink-0 flex items-center justify-center h-10 w-10 rounded-xl bg-purple-50 shadow-neomorph-inset sm:mx-0 sm:h-8 sm:w-8">
                  <Database className="h-5 w-5 text-purple-600" />
                </div>
                <div className="mt-2 text-center sm:mt-0 sm:ml-3 sm:text-left w-full">
                  <h3 className="text-base font-semibold text-gray-800">
                    Switch {confirmState.network} provider?
                  </h3>
                  <div className="mt-1.5 space-y-1.5">
                    <p className="text-xs text-gray-600">
                      You are about to change the data provider for{' '}
                      <span className="font-semibold text-gray-800">
                        {confirmState.network}
                      </span>
                      :
                    </p>
                    <div className="flex items-center gap-2 text-xs">
                      <span className="px-2 py-0.5 rounded bg-gray-100 text-gray-600 border border-gray-200">
                        {confirmState.oldProvider}
                      </span>
                      <span className="text-gray-400">→</span>
                      <span className="px-2 py-0.5 rounded bg-primary-50 text-primary-700 border border-primary-200 font-semibold">
                        {confirmState.newProvider}
                      </span>
                    </div>
                    <p className="text-[11px] text-gray-500 pt-1">
                      Users will see the new provider&apos;s {confirmState.network}{' '}
                      plans on their next page load. This takes effect immediately.
                    </p>
                  </div>
                </div>
              </div>

              <div className="mt-4 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  onClick={handleConfirm}
                  disabled={isLoading}
                  className="w-full inline-flex justify-center rounded-lg border border-transparent px-3 py-1.5 bg-gradient-to-r from-primary-600 to-primary-700 text-xs font-medium text-white shadow-md shadow-primary-500/30 hover:shadow-lg focus:outline-none sm:ml-3 sm:w-auto disabled:opacity-50"
                >
                  {isLoading ? 'Switching...' : 'Confirm Switch'}
                </button>
                <button
                  type="button"
                  onClick={handleCancel}
                  disabled={isLoading}
                  className="mt-2 w-full inline-flex justify-center rounded-lg border border-gray-200 px-3 py-1.5 bg-white text-xs font-medium text-gray-700 shadow-neomorph hover:shadow-neomorph-hover focus:outline-none sm:mt-0 sm:w-auto"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Airtime switch confirm modal */}
      {airtimeConfirm && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-3 pt-3 pb-16 text-center sm:block sm:p-0">
            <div
              className="fixed inset-0 transition-opacity bg-gray-600/50 backdrop-blur-sm"
              onClick={() => setAirtimeConfirm(null)}
            />

            <div className="inline-block align-bottom bg-[#E8ECF5] rounded-xl shadow-neomorph px-4 pt-4 pb-4 text-left overflow-hidden transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-5 border border-primary-100/30">
              <div className="sm:flex sm:items-start">
                <div className="mx-auto flex-shrink-0 flex items-center justify-center h-10 w-10 rounded-xl bg-green-50 shadow-neomorph-inset sm:mx-0 sm:h-8 sm:w-8">
                  <Database className="h-5 w-5 text-green-600" />
                </div>
                <div className="mt-2 text-center sm:mt-0 sm:ml-3 sm:text-left w-full">
                  <h3 className="text-base font-semibold text-gray-800">
                    Switch airtime provider?
                  </h3>
                  <div className="mt-1.5 space-y-1.5">
                    <p className="text-xs text-gray-600">
                      All new airtime purchases will be fulfilled by:
                    </p>
                    <div className="flex items-center gap-2 text-xs">
                      <span className="px-2 py-0.5 rounded bg-gray-100 text-gray-600 border border-gray-200">
                        {airtimeProviderConfig?.providerType}
                      </span>
                      <span className="text-gray-400">→</span>
                      <span className="px-2 py-0.5 rounded bg-green-50 text-green-700 border border-green-200 font-semibold">
                        {airtimeConfirm}
                      </span>
                    </div>
                    <p className="text-[11px] text-gray-500 pt-1">
                      Takes effect immediately. Existing in-flight transactions
                      complete on their original provider.
                    </p>
                  </div>
                </div>
              </div>

              <div className="mt-4 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  disabled={isLoading}
                  onClick={async () => {
                    try {
                      await updateAirtimeProviderConfig(airtimeConfirm);
                      setAirtimeConfirm(null);
                    } catch {
                      setAirtimeConfirm(null);
                    }
                  }}
                  className="w-full inline-flex justify-center rounded-lg border border-transparent px-3 py-1.5 bg-gradient-to-r from-green-600 to-green-700 text-xs font-medium text-white shadow-md shadow-green-500/30 hover:shadow-lg focus:outline-none sm:ml-3 sm:w-auto disabled:opacity-50"
                >
                  {isLoading ? 'Switching...' : 'Confirm Switch'}
                </button>
                <button
                  type="button"
                  disabled={isLoading}
                  onClick={() => setAirtimeConfirm(null)}
                  className="mt-2 w-full inline-flex justify-center rounded-lg border border-gray-200 px-3 py-1.5 bg-white text-xs font-medium text-gray-700 shadow-neomorph hover:shadow-neomorph-hover focus:outline-none sm:mt-0 sm:w-auto"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DataProviderConfigTable;